import type { AuthMode, CustomerConfig } from '../types'

export type ResolvedIdentityMode =
  | { kind: 'signed'; email: string; name?: string; userHash: string; userHashTimestamp: number }
  | { kind: 'unsigned'; email: string; name?: string }
  | { kind: 'sso' }
  | { kind: 'email-prompt' }
  | { kind: 'error'; reason: 'no-auth-configured' }

export interface ResolveIdentityArgs {
  customer?: CustomerConfig | null
  serverAllowed: AuthMode[]
  override?: AuthMode | AuthMode[]
  isSsoAuthenticated: boolean
}

const KNOWN_MODES: readonly AuthMode[] = ['sso', 'email', 'signed']
const SAFE_DEFAULT: readonly AuthMode[] = ['sso', 'email']

/**
 * Single source of truth that decides which identity UX the widget should show next. Pure function;
 * trivially unit-testable. See `sdk/widget/src/auth/identity-resolver.test.ts` for coverage of every
 * branch in the decision table below.
 *
 * Precedence:
 *   1. If the host user is already SSO-authenticated, always use SSO.
 *   2. effectiveAllowed = intersection(serverAllowed, override ?? serverAllowed). If the result is
 *      empty we fall back to the safe default so the widget is never completely blocked.
 *   3. signed identity present and allowed → `signed`.
 *   4. plain email (no userHash) present and email mode allowed → `unsigned`.
 *   5. SSO allowed → `sso`.
 *   6. email allowed → `email-prompt`.
 *   7. otherwise → `error: no-auth-configured`.
 */
export function resolveIdentityMode(args: ResolveIdentityArgs): ResolvedIdentityMode {
  if (args.isSsoAuthenticated) {
    return { kind: 'sso' }
  }

  const sanitizedServer = sanitizeModes(args.serverAllowed)
  const baseServer = sanitizedServer.length > 0 ? sanitizedServer : [...SAFE_DEFAULT]
  const override = normalizeOverride(args.override)
  const effective = override.length > 0
    ? baseServer.filter(mode => override.includes(mode))
    : baseServer

  // If the narrowing produced an empty set (host and server disagree entirely), we keep the server's
  // allowed list so the widget can still render something the admin approved.
  const allowed = effective.length > 0 ? effective : baseServer

  const email = args.customer?.email?.trim()
  const name = args.customer?.name
  const userHash = args.customer?.userHash
  const userHashTimestamp = args.customer?.userHashTimestamp

  if (email && userHash && typeof userHashTimestamp === 'number' && allowed.includes('signed')) {
    return { kind: 'signed', email, name, userHash, userHashTimestamp }
  }

  // If the host supplied an email but the signature is unusable (signed mode not allowed, or no
  // userHash), fall through to the unsigned path when email mode is allowed. This matches FR-024:
  // "When the host app has supplied a signed identity but signed mode is NOT allowed on the project,
  // the widget MUST NOT submit the signed identity and MUST fall back to whichever other modes are
  // allowed."
  if (email && allowed.includes('email')) {
    return { kind: 'unsigned', email, name }
  }

  if (allowed.includes('sso')) {
    return { kind: 'sso' }
  }

  if (allowed.includes('email')) {
    return { kind: 'email-prompt' }
  }

  return { kind: 'error', reason: 'no-auth-configured' }
}

function sanitizeModes(modes: AuthMode[] | undefined | null): AuthMode[] {
  if (!Array.isArray(modes)) return []
  const unique = new Set<AuthMode>()
  for (const mode of modes) {
    if (KNOWN_MODES.includes(mode as AuthMode)) unique.add(mode as AuthMode)
  }
  return Array.from(unique)
}

function normalizeOverride(override: AuthMode | AuthMode[] | undefined): AuthMode[] {
  if (!override) return []
  if (Array.isArray(override)) return sanitizeModes(override)
  return KNOWN_MODES.includes(override as AuthMode) ? [override as AuthMode] : []
}
